/**
 * Hydration Steps Swiper
 * Handles Swiper initialization for the hydration steps section
 */

class HydrationStepsSwiper {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.enableSliderDesktop = element.dataset.enableSliderDesktop === 'true';

    this.init();
  }

  init() {
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    // Check if we should initialize Swiper based on screen size
    this.checkAndInitialize();

    // Handle window resize
    this.handleResize();
  }

  checkAndInitialize() {
    const isDesktop = window.innerWidth >= 990;

    // Initialize Swiper if:
    // 1. We're on mobile/tablet (always enabled)
    // 2. We're on desktop AND slider is enabled for desktop
    if (!isDesktop || (isDesktop && this.enableSliderDesktop)) {
      this.initializeSwiper();
    } else {
      // Destroy Swiper if it exists and we're on desktop without slider enabled
      this.destroySwiper();
    }
  }

  initializeSwiper() {
    // Don't initialize if already exists
    if (this.swiper) return;

    const swiperElement = this.element.querySelector('[data-swiper-main]');
    if (!swiperElement) return;

    const isDesktop = window.innerWidth >= 990;
    const isTablet = window.innerWidth >= 750 && window.innerWidth < 990;
    const isMobile = window.innerWidth < 750;

    // Determine slides per view based on screen size
    let slidesPerView = 1.2;
    if (isDesktop && this.enableSliderDesktop) {
      slidesPerView = 3;
    } else if (isTablet) {
      slidesPerView = 2;
    } else if (isMobile) {
      slidesPerView = 1.2;
    }

    const config = {
      slidesPerView: slidesPerView,
      spaceBetween: parseInt(getComputedStyle(this.element).getPropertyValue('--gap-between-blocks')) || 24,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      keyboard: {
        enabled: true,
      },
      a11y: {
        enabled: true,
      },
      breakpoints: {
        // Mobile
        320: {
          slidesPerView: 1.2,
          spaceBetween: parseInt(getComputedStyle(this.element).getPropertyValue('--gap-between-blocks')) || 20,
        },
        // Tablet
        750: {
          slidesPerView: 2,
          spaceBetween: parseInt(getComputedStyle(this.element).getPropertyValue('--gap-between-blocks')) || 24,
        },
        // Desktop (only if slider is enabled)
        990: {
          slidesPerView: this.enableSliderDesktop ? 3 : 3,
          spaceBetween: parseInt(getComputedStyle(this.element).getPropertyValue('--gap-between-blocks')) || 24,
        }
      },
      on: {
        init: () => {
          this.updateNavigationVisibility();
        },
        slideChange: () => {
          this.updateNavigationVisibility();
        },
        resize: () => {
          this.updateNavigationVisibility();
        }
      }
    };

    this.swiper = new Swiper(swiperElement, config);
  }

  updateNavigationVisibility() {
    if (!this.swiper) return;

    const prevButton = this.element.querySelector('.swiper-button-prev');
    const nextButton = this.element.querySelector('.swiper-button-next');

    // Hide navigation if all slides are visible
    if (this.swiper.isBeginning && this.swiper.isEnd) {
      if (prevButton) prevButton.style.display = 'none';
      if (nextButton) nextButton.style.display = 'none';
    } else {
      if (prevButton) prevButton.style.display = 'flex';
      if (nextButton) nextButton.style.display = 'flex';
    }
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.checkAndInitialize();
      }, 250);
    });
  }
}

// Initialize all hydration steps sliders when DOM is ready
function initializeHydrationStepsSliders() {
  const sliders = document.querySelectorAll('[data-hydration-steps-slider]');

  sliders.forEach(slider => {
    if (!slider.swiperInstance) {
      slider.swiperInstance = new HydrationStepsSwiper(slider);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHydrationStepsSliders);
} else {
  initializeHydrationStepsSliders();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const sliders = event.target.querySelectorAll('[data-hydration-steps-slider]');
  sliders.forEach(slider => {
    if (slider.swiperInstance) {
      slider.swiperInstance.destroySwiper();
    }
    slider.swiperInstance = new HydrationStepsSwiper(slider);
  });
});

// Cleanup on Shopify section unload
document.addEventListener('shopify:section:unload', (event) => {
  const sliders = event.target.querySelectorAll('[data-hydration-steps-slider]');
  sliders.forEach(slider => {
    if (slider.swiperInstance) {
      slider.swiperInstance.destroySwiper();
      delete slider.swiperInstance;
    }
  });
});
